import { Module, MutationTree, ActionTree, GetterTree } from 'vuex';
import { RootStore } from '@/features/core/store';
import { HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items } from '@/hsc-api/queries/__generated__/HeatingSystemCollectionWithPaginationQuery';

export type ControlModel = {
  name: string;
  heatingSystemId: string;
  deviceSerialNumber: string;
};

export type FilteringStatus = 'inProgress' | 'change' | 'edit' | 'open' | null;

export type DataType = 'float16' | 'float32' | 'int16' | 'uint16' | 'uint32';
export type TableData = 'table' | 'graph' | 'edit';

export type Register = {
  registerNumber: string;
  registerName: string;
  registerFormat: string;
  metricName: string;
  unit: string;
  dataType: DataType;
  offset: number;
  scaleFactor: number | null;
  functionCode: number;
  intervalSeconds: number;
  tableData: TableData;
  standardMetric: boolean;
};

export type Device = {
  deviceRole: string;
  deviceManufacturer: string;
  deviceModel: string;
  deviceVersion: string;
  registers: Register[];
};

export type ApiResponse = Device[];

export type RegisterValue = {
  registerName: string;
  value: number;
};

export type SaveRegisterRequest = {
  deviceRole: string;
  manufacturer: string;
  model: string;
  version?: string;
  registers: (string | number)[];
};

export type ControlModelName = {
  name: string;
};

type HeatingSystemControlValues = {
  allRegisters: { registerName: string; value: number }[];
  // Heating systems
  activeHeatingSystemId: string | null;
  heatingSystems: HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items[];
  heatingSystemSearchTerm: string;
  heatingSystemFilteringStatus: FilteringStatus;

  // Control types
  controlModels: ControlModel[];

  // Clusters
  activeClusterName: string | null;
  clusters: any[];

  // Register values
  devices: Device[];
  registerValues: RegisterValue[];
};

// Initial state of the project
const state: HeatingSystemControlValues = {
  activeHeatingSystemId: null,
  heatingSystemSearchTerm: '',
  heatingSystemFilteringStatus: null,
  activeClusterName: null,
  controlModels: [],
  heatingSystems: [],
  clusters: [],
  registerValues: [],
  devices: [],
  allRegisters: [],
};

// Mutations
const mutations: MutationTree<HeatingSystemControlValues> = {
  setControlModels(state, controlModels: ControlModel[]) {
    state.controlModels = controlModels;
  },
  setHeatingSystemSearchTerm(state, searchTerm: string) {
    state.heatingSystemSearchTerm = searchTerm;
  },
  setHeatingSystemFilteringStatus(state, status: FilteringStatus) {
    state.heatingSystemFilteringStatus = status;
  },
  setActiveCluster(state, activeClusterName: string) {
    state.activeClusterName = activeClusterName;
  },
  setClusters(state, clusters: any[]) {
    state.clusters = clusters;
  },
  setHeatingSystems(state, heatingSystems: HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items[]) {
    state.heatingSystems = heatingSystems;
  },
  setDevices(state, devices: Device[]) {
    state.devices = devices;
  },
  setRegisterValues(state, registerValues: RegisterValue[]) {
    state.registerValues = registerValues;
  },
  setAllRegisters(state, registers: { registerName: string; value: number }[]) {
    state.allRegisters = registers;
  },
};

// Actions
const actions: ActionTree<HeatingSystemControlValues, any> = {};

// Getters
const getters: GetterTree<HeatingSystemControlValues, RootStore> = {
  filteredControlModels: (state) => {
    return state.controlModels;
  },
  activeClusterName: (state) => {
    return state.activeClusterName;
  },
};

// Export the module
export const EditHeatingSystemRegistersModule: Module<HeatingSystemControlValues, any> = {
  state,
  mutations,
  actions,
  getters,
};
